import { inject, Injectable, makeStateKey, TransferState } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, tap, timeout } from 'rxjs';
import { SLIDES_DATA, GallerySlideContent } from './gallery-data';
import { HomeSlideApi, toGallerySlide } from './slide-html-builder';
import { AppConfigService, httpOptions, Log } from '@core/services/config/app-config.service';
import { SeasonalThemeService } from '@core/services/theme/seasonal-theme.service';

const HOME_SLIDES_KEY = makeStateKey<GallerySlideContent[]>('home.slides');

// L'hero è l'elemento LCP: oltre questo tempo si rinuncia all'API e si usano le slide statiche
const API_TIMEOUT_MS = 3000;

@Injectable({
    providedIn: 'root'
})
export class SlideConfigService {

    private http = inject(HttpClient);
    private appConfig = inject(AppConfigService);
    private seasonalTheme = inject(SeasonalThemeService);
    private transferState = inject(TransferState);

    private slides: GallerySlideContent[] | null = null;

    /**
     * Carica le slide da API (una volta per app; chiamato dal resolver della home).
     * In SSR il risultato passa al client via TransferState: hydration con gli stessi dati, nessuna
     * seconda chiamata. Se l'API fallisce o non restituisce slide si usano quelle statiche.
     */
    load(): Observable<GallerySlideContent[]> {
        if (this.slides) {
            return of(this.slides);
        }

        if (this.transferState.hasKey(HOME_SLIDES_KEY)) {
            this.slides = this.transferState.get(HOME_SLIDES_KEY, []);
            return of(this.slides);
        }

        const url = this.appConfig.apiUrl() + '/getHomeSlides/' + this.appConfig.lang() + '/' + this.appConfig.domain();

        return this.http.get<HomeSlideApi[]>(url, httpOptions).pipe(
            timeout(API_TIMEOUT_MS),
            map((slides) => (Array.isArray(slides) ? slides : []).map(toGallerySlide)),
            catchError((error) => {
                Log.error('Errore caricamento slide home, uso le slide statiche:', error);
                return of([]);
            }),
            map((slides) => (slides.length > 0 ? slides : this.staticSlides())),
            tap((slides) => {
                this.slides = slides;
                if (this.appConfig.ssr()) {
                    this.transferState.set(HOME_SLIDES_KEY, slides);
                }
            })
        );
    }

    getSlides(): GallerySlideContent[] {
        return this.slides ?? this.staticSlides();
    }

    // Slide statiche di fallback; le stagionali compaiono solo nel loro periodo (stessa data in SSR e client).
    private staticSlides(): GallerySlideContent[] {
        return SLIDES_DATA
            .filter((slide) => !slide.season || this.seasonalTheme[slide.season])
            .map((slide) => ({ ...slide, html_content: this.replacePlaceholder(slide) }));
    }

    // Sostituisce i placeholder {{key}} con le traduzioni, senza modificare SLIDES_DATA
    // (in SSR il modulo è condiviso tra richieste di lingue diverse).
    private replacePlaceholder(slide: GallerySlideContent): string {
        return slide.trad_keys.reduce((html, key) => {
            const placeholder = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
            return html.replace(placeholder, this.appConfig.i18n[key]);
        }, slide.html_content);
    }
}
